<template>
  <div>
    <div class="d-flex justify-content-between">
      <div class="d-flex">
        <!-- avatar -->
        <span class="avatar-title rounded-circle avatar-sm" v-if="!review.author.photo">
          {{ review.author.name | acronym }}
        </span>
        <img
          alt="authImg"
          class="avatar-img rounded-circle avatar-sm"
          :src="review.author.photo"
          v-else
        />

        <!-- customerName/rating -->
        <div class="ml-3">
          <h4 class="card-title mb-2">{{ review.author.name }}</h4>
          <div class="stars float-left mr-2" style="height: 22.5px">
            <StarRating :rating="review.rating" v-if="network !== 'facebook'" />
          </div>
        </div>
      </div>
      <span>
        <img
          :src="'/theme/img/platforms/' + network + '.svg'"
          alt="..."
          class="avatar-img avatar-xs rounded"
        />
      </span>
    </div>

    <!-- text -->
    <div class="mt-2" :class="{ 'mb-2': hasReplies, 'mb-6': !hasReplies }">
      <p class="review-text mb-0" v-if="review.text && review.text.length">
        <read-more
          more-str="read more"
          :text="review.text"
          link="#"
          less-str=" show less"
          :max-chars="200"
        ></read-more>
      </p>
      <p class="text-muted review-text" :class="{ 'mb-0': hasReplies, 'mb-3': !hasReplies }" v-else>
        <i>Author did not leave a comment.</i>
      </p>
    </div>

    <!-- location and datetime below text when replies exist -->
    <div v-if="hasReplies" class="mb-4">
      <div class="d-flex text-muted mb-3">
        <span>
          <span class="badge badge-light mr-2"> {{ this.locationName }}</span>
          <span class="fe fe-clock"></span>&nbsp;
          <time v-if="review.timestamp">
            {{ $moment(review.timestamp).format('ll') }}
          </time>
        </span>
      </div>

      <!-- replies section -->
      <div>
        <div
          v-for="reply in displayedReplies"
          :key="reply.id || reply._id"
          class="reply-container ml-5 mb-2"
        >
          <div
            class="card p-3 mb-0 border-right-1 border-top-1 border-bottom-1"
            style="border-left: 2px solid #007bff !important"
          >
            <div class="d-flex justify-content-between mb-3">
              <div class="d-flex align-items-center">
                <!-- avatar -->
                <span class="avatar-title rounded-circle avatar-sm" v-if="!reply.author.photo">
                  {{ reply.author.name | acronym }}
                </span>
                <img
                  alt="authImg"
                  class="avatar-img rounded-circle avatar-sm"
                  :src="reply.author.photo"
                  v-else
                />

                <!-- response author -->
                <div class="ml-3">
                  <h4 class="card-title">{{ reply.author.name }}</h4>
                </div>
              </div>
            </div>

            <p class="review-text mb-0" v-if="reply.text && reply.text.length">
              <read-more
                more-str="read more"
                :text="reply.text"
                link="#"
                less-str=" show less"
                :max-chars="200"
              ></read-more>
            </p>

            <div class="d-flex text-muted mt-3" v-if="reply.timestamp">
              <span>
                <span class="fe fe-clock"></span>&nbsp;
                <time>
                  {{ $moment(reply.timestamp).format('ll') }}
                </time>
              </span>
            </div>
          </div>
        </div>

        <!-- See more / Show less button -->
        <div v-if="hasMultipleReplies" class="ml-5">
          <button class="btn btn-link p-0" @click="showAllReplies = !showAllReplies">
            {{
              showAllReplies
                ? 'Show less'
                : `See more (${review.replies.length - 1} ${
                    review.replies.length - 1 === 1 ? 'reply' : 'replies'
                  })`
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- footer -->
    <div style="position: absolute; bottom: 24px; width: inherit" v-if="!hasReplies">
      <div class="d-flex justify-content-between text-muted">
        <span>
          <span class="badge badge-light mr-2"> {{ this.locationName }}</span>
          <span class="fe fe-clock"></span>&nbsp;
          <time v-if="review.timestamp">
            {{ this.$moment(review.timestamp).format('ll') }}
          </time>
        </span>
      </div>
    </div>
    <div
      style="position: absolute; bottom: 24px; right: 24px"
      v-if="!selectIsClickoutBlacklisted(this.network)"
    >
      <div class="d-flex justify-content-between text-muted">
        <button class="btn btn-link p-1 text-muted" @click="handleClickOut">
          <ExternalLinkIcon size="1.2x"></ExternalLinkIcon>
        </button>
      </div>
    </div>

    <GoogleProfileIdModal
      ref="googleIdModal"
      @save="openManually"
      :location="reviewLocation"
      :reviewSubscription="reviewSubscription"
      v-if="network === 'google' && !hasGoogleBusinessProfileId"
    />
  </div>
</template>

<script>
import { ExternalLinkIcon } from 'vue-feather-icons'
import StarRating from '@/components/Modules/Review/StarRating'
import GoogleProfileIdModal from '@/components/Modals/GoogleProfileIdModal'
import { createNamespacedHelpers } from 'vuex'
import config from '@/config'

const LocationModule = createNamespacedHelpers('location')
const ReviewModule = createNamespacedHelpers('review')

export default {
  name: 'ReviewCard',
  props: {
    review: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showAllReplies: false
    }
  },
  components: {
    StarRating,
    ExternalLinkIcon,
    GoogleProfileIdModal
  },
  filters: {
    acronym(value) {
      if (!value) return ''
      return value.match(/\b\w/g).join('').toUpperCase().substring(0, 2)
    }
  },
  computed: {
    ...LocationModule.mapGetters(['selectLocationName', 'selectActiveLocations']),
    ...ReviewModule.mapGetters(['selectIsClickoutBlacklisted']),
    reviewLocation() {
      const location = this.selectActiveLocations.find((l) => {
        if (l.subscriptions && this.reviewSubscription) {
          return l.subscriptions.find(
            (sub) =>
              sub.reviewSubscription?._id === this.reviewSubscription?._id &&
              sub.type === 'review-subscription'
          )
            ? true
            : false
        }
        return false
      })
      return location
    },
    locationName() {
      if (this.reviewLocation) {
        return this.selectLocationName(this.reviewLocation?._id)
      }
      return ''
    },
    reviewSubscription() {
      const reviewSubscriptionMap = this.selectActiveLocations.reduce((data, l) => {
        if (l.subscriptions) {
          l.subscriptions.forEach((s) => {
            if (s.type === 'review-subscription' && s.reviewSubscription) {
              data[s.reviewSubscription?._id] = s.reviewSubscription
            }
          })
        }
        return data
      }, {})

      return reviewSubscriptionMap[this.review.reviewSubscription]
    },
    hasGoogleBusinessProfileId() {
      return this.reviewSubscription?.googleBusinessProfileId
    },
    network() {
      return this.reviewSubscription?.network
    },
    hasReplies() {
      return this.review.replies && this.review.replies.length
    },
    hasMultipleReplies() {
      return this.review.replies && this.review.replies.length > 1
    },
    selectedReply() {
      if (!this.hasReplies) return null

      // Find restaurant replies by comparing names
      const restaurantReplies = this.review.replies.filter((reply) => {
        if (!reply.author?.name || !this.reviewSubscription?.name) {
          return false
        }

        const replyAuthorName = reply.author.name.toLowerCase()
        const subscriptionName = this.reviewSubscription.name.toLowerCase()

        return replyAuthorName === subscriptionName || subscriptionName.includes(replyAuthorName)
      })

      if (restaurantReplies.length > 0) {
        // Sort by timestamp and return the most recent
        return [...restaurantReplies].sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0
          return timeB - timeA // Descending order (most recent first)
        })[0]
      }

      // If no restaurant replies, return the first reply
      return this.review.replies[0]
    },
    sortedReplies() {
      if (!this.hasReplies) return []

      // Return replies sorted by timestamp ascending (oldest first)
      return [...this.review.replies].sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0
        return timeA - timeB
      })
    },
    displayedReplies() {
      return this.showAllReplies ? this.sortedReplies : [this.selectedReply]
    }
  },
  methods: {
    ...LocationModule.mapActions(['addGoogleBusinessProfileId']),
    ...ReviewModule.mapActions(['refreshList']),
    handleClickOut() {
      if (['yelp', 'facebook'].includes(this.network)) {
        window.parent.postMessage({ openUrl: this.review.replyUrl }, config.baseAppUrl)
      } else if (this.network === 'google') {
        if (this.hasGoogleBusinessProfileId) {
          const replyUrl = this.createGoogleReplyUrl(
            this.reviewSubscription?.googleBusinessProfileId,
            this.review.id
          )
          window.parent.postMessage({ openUrl: replyUrl }, config.baseAppUrl)
        } else {
          this.$refs.googleIdModal.open()
        }
      } else if (this.network === 'tripadvisor') {
        const oldReplyUrl = this.review.replyUrl
        const regex1 = /ShowUserReviews/
        const regex2 = /-r/
        let newReplyUrl = oldReplyUrl.replace(regex1, 'ReputationManager')
        newReplyUrl = newReplyUrl.replace(regex2, '-a_reviewId.')
        window.parent.postMessage({ openUrl: newReplyUrl }, config.baseAppUrl)
      }
    },
    openManually(profileId) {
      this.addGoogleBusinessProfileId({
        locationId: this.reviewLocation._id,
        reviewSubscription: this.review.reviewSubscription,
        googleBusinessProfileId: profileId
      })
      const replyUrl = this.createGoogleReplyUrl(profileId, this.review.id)
      window.parent.postMessage({ openUrl: replyUrl }, config.baseAppUrl)
      this.refreshList()
    },
    createGoogleReplyUrl(profileId, googleReviewId) {
      return `https://business.google.com/n/${profileId}/reviews/${googleReviewId}`
    }
  }
}
</script>

<style lang="scss">
.review-text p {
  display: inline;
}

.badge {
  font-size: 13px;
  margin-bottom: 2px;
}
</style>
